/**
 * Exchange state - per-coin simulated market + player portfolio + listing status.
 * Pure data only (no window/document/PIXI).
 *
 * "Living market": the coin pool is 30, but only listedSlots (8) are listed at
 * once. Delisted / pool coins keep a MINIMAL market (last price, no candles) so
 * the save stays small; only listed coins carry full candle history.
 */

import { COINS, TIMEFRAMES, getCoinConfig, type Timeframe } from './config/coins.js';
import { LISTING_CONFIG } from './config/listing.js';

/** OHLC candle. t = bucket start timestamp (ms) */
export interface Candle {
  t: number;
  o: number;
  h: number;
  l: number;
  c: number;
}

/** Per-coin simulated market state */
export interface CoinMarket {
  price: number;
  /** Current momentum as a price fraction per tick */
  momentum: number;
  /** Candle history per timeframe. Last element = forming candle */
  candles: Record<Timeframe, Candle[]>;
}

/** Player holding for one coin */
export interface ExchangeHolding {
  amount: number;
  avgPrice: number;
}

export type ListingStatus = 'listed' | 'delisted' | 'pool';

/** Where a coin sits in the listing lifecycle */
export interface CoinListing {
  status: ListingStatus;
  /** when it was most recently listed (0 = never) */
  listedAt: number;
  /** when it was most recently delisted (0 = never delisted / still listed) */
  delistedAt: number;
  /** how many times this coin has revived (drives the "N.0" rebrand suffix) */
  revivedCount: number;
  /** true once the coin has ever been delisted (kept for tombstone / UI hints) */
  everDelisted: boolean;
}

export interface ExchangeState {
  coins: Record<string, CoinMarket>;
  listing: Record<string, CoinListing>;
  holdings: Record<string, ExchangeHolding>;
  lastTickTime: number;
  /** next time the listing system may run a transition */
  lastListingTransition: number;
  /** next time delist conditions are re-evaluated */
  lastListingEval: number;
  totalTrades: number;
  realizedProfit: number;
}

/** Ad hook funnel counters (Sprint 1: logging infra only) */
export interface AdHookStat {
  shown: number;
  accepted: number;
}

function fullCandles(startPrice: number, now: number): Record<Timeframe, Candle[]> {
  const candles = {} as Record<Timeframe, Candle[]>;
  for (const tf of Object.keys(TIMEFRAMES) as Timeframe[]) {
    const bucket = Math.floor(now / TIMEFRAMES[tf]) * TIMEFRAMES[tf];
    candles[tf] = [{ t: bucket, o: startPrice, h: startPrice, l: startPrice, c: startPrice }];
  }
  return candles;
}

function emptyCandles(): Record<Timeframe, Candle[]> {
  const candles = {} as Record<Timeframe, Candle[]>;
  for (const tf of Object.keys(TIMEFRAMES) as Timeframe[]) candles[tf] = [];
  return candles;
}

/** Full market with a seeded candle history (listed coins). */
export function createCoinMarket(startPrice: number, now: number): CoinMarket {
  return { price: startPrice, momentum: 0, candles: fullCandles(startPrice, now) };
}

/** Minimal market for a delisted / pool coin: last price only, no candles. */
export function minimalCoinMarket(price: number): CoinMarket {
  return { price, momentum: 0, candles: emptyCandles() };
}

/** True when a coin currently occupies an exchange slot. */
export function isListed(ex: ExchangeState, coinId: string): boolean {
  return ex.listing[coinId]?.status === 'listed';
}

/** Ids of all currently listed coins (config order). */
export function listedCoinIds(ex: ExchangeState): string[] {
  return COINS.filter(c => ex.listing[c.id]?.status === 'listed').map(c => c.id);
}

/** Listed meme coin ids (majors excluded) - drives risk/whale/scenario systems. */
export function listedMemeIds(ex: ExchangeState): string[] {
  return COINS.filter(c => c.tier === 'meme' && ex.listing[c.id]?.status === 'listed').map(c => c.id);
}

export function createInitialExchangeState(): ExchangeState {
  const now = Date.now();
  const coins: Record<string, CoinMarket> = {};
  const listing: Record<string, CoinListing> = {};

  // Majors are always listed. Fill remaining slots with the first N meme coins.
  const majors = COINS.filter(c => c.permanent);
  const memes = COINS.filter(c => !c.permanent);
  const memeSlots = Math.max(0, LISTING_CONFIG.listedSlots - majors.length);

  for (let i = 0; i < memes.length; i++) {
    const cfg = memes[i];
    const startListed = i < memeSlots;
    coins[cfg.id] = startListed ? createCoinMarket(cfg.startPrice, now) : minimalCoinMarket(cfg.startPrice);
    listing[cfg.id] = {
      status: startListed ? 'listed' : 'pool',
      listedAt: startListed ? now : 0,
      delistedAt: 0, revivedCount: 0, everDelisted: false,
    };
  }
  for (const cfg of majors) {
    coins[cfg.id] = createCoinMarket(cfg.startPrice, now);
    listing[cfg.id] = { status: 'listed', listedAt: now, delistedAt: 0, revivedCount: 0, everDelisted: false };
  }

  return {
    coins, listing, holdings: {},
    lastTickTime: now, lastListingTransition: now, lastListingEval: now,
    totalTrades: 0, realizedProfit: 0,
  };
}

function migrateListing(saved: Partial<CoinListing> | undefined, fallback: CoinListing): CoinListing {
  if (!saved || typeof saved.status !== 'string') return fallback;
  return {
    status: saved.status,
    listedAt: saved.listedAt ?? fallback.listedAt,
    delistedAt: saved.delistedAt ?? 0,
    revivedCount: saved.revivedCount ?? 0,
    everDelisted: saved.everDelisted ?? false,
  };
}

/**
 * Migrate a loaded save (backward compatible).
 * Legacy saves (pre-listing) only have the original coins and no `listing`
 * map: those coins stay listed, every other pool coin starts as `pool`.
 * Holdings are always preserved for coins still in the config.
 */
export function migrateExchangeState(saved: Partial<ExchangeState> | undefined): ExchangeState {
  const fresh = createInitialExchangeState();
  if (!saved) return fresh;
  const hasListing = !!saved.listing && typeof saved.listing === 'object';

  const now = Date.now();
  const poolListing: CoinListing = { status: 'pool', listedAt: 0, delistedAt: 0, revivedCount: 0, everDelisted: false };

  const coins: Record<string, CoinMarket> = {};
  const listing: Record<string, CoinListing> = {};
  for (const cfg of COINS) {
    let lst: CoinListing;
    if (hasListing) {
      // Modern save: keep the saved listing status verbatim.
      lst = migrateListing(saved.listing?.[cfg.id], fresh.listing[cfg.id] ?? poolListing);
    } else if (cfg.permanent) {
      // Legacy save without a listing map: majors are always listed.
      lst = { status: 'listed', listedAt: saved.lastTickTime ?? now, delistedAt: 0, revivedCount: 0, everDelisted: false };
    } else if (saved.coins?.[cfg.id]) {
      // A meme that had a saved market = it was one of the old listed coins.
      lst = { status: 'listed', listedAt: saved.lastTickTime ?? now, delistedAt: 0, revivedCount: 0, everDelisted: false };
    } else {
      // Every other pool coin was never listed in the legacy save.
      lst = { ...poolListing };
    }
    listing[cfg.id] = lst;

    const savedCoin = saved.coins?.[cfg.id];
    if (savedCoin && typeof savedCoin.price === 'number') {
      coins[cfg.id] = lst.status === 'listed'
        ? {
            price: savedCoin.price,
            momentum: savedCoin.momentum ?? 0,
            candles: {
              m1: savedCoin.candles?.m1 ?? fresh.coins[cfg.id]?.candles.m1 ?? [],
              m5: savedCoin.candles?.m5 ?? fresh.coins[cfg.id]?.candles.m5 ?? [],
              h1: savedCoin.candles?.h1 ?? fresh.coins[cfg.id]?.candles.h1 ?? [],
            },
          }
        : minimalCoinMarket(savedCoin.price);
    } else {
      const startPrice = getCoinConfig(cfg.id)?.startPrice ?? 1;
      coins[cfg.id] = lst.status === 'listed'
        ? createCoinMarket(startPrice, now)
        : minimalCoinMarket(startPrice);
    }
  }

  // Drop holdings for coins no longer in the config (e.g. renamed coins)
  const holdings: ExchangeState['holdings'] = {};
  for (const [coinId, holding] of Object.entries(saved.holdings ?? {})) {
    if (coins[coinId]) holdings[coinId] = holding;
  }

  return {
    coins, listing, holdings,
    lastTickTime: saved.lastTickTime ?? Date.now(),
    lastListingTransition: saved.lastListingTransition ?? Date.now(),
    lastListingEval: saved.lastListingEval ?? Date.now(),
    totalTrades: saved.totalTrades ?? 0,
    realizedProfit: saved.realizedProfit ?? 0,
  };
}

/**
 * 24h price change percent, approximated from h1 candle history.
 * Falls back to the earliest available candle when history is short.
 */
export function getChange24h(coin: CoinMarket): number {
  const h1 = coin.candles.h1;
  if (h1.length === 0) return 0;
  const idx = Math.max(0, h1.length - 25);
  const base = idx === 0 ? h1[0].o : h1[idx].c;
  if (base <= 0) return 0;
  return ((coin.price - base) / base) * 100;
}
